import {
    Analytics,
    EventEmitter,
    ISponsorshipService,
    SponsoredShow,
    SponsoredShowPlacementsList
} from "common";
import { SponsorshipError } from "./SponsorshipError";

declare const BASE_API_URL: string;

export class SponsorshipService
    extends EventEmitter
    implements ISponsorshipService
{
    private static TAG_DATA_ATTRIBUTE = "data-sponsorship-collection";

    private readonly observer: MutationObserver;

    private data: SponsoredShow | null;

    private tag: string | null;

    constructor() {
        super();
        this.tag = SponsorshipService.getTagByBodyAttribute();
        this.data = null;
        this.observer = new MutationObserver(this.mutationCallback);
    }

    private static getTagByBodyAttribute = () =>
        document.body.getAttribute(`${SponsorshipService.TAG_DATA_ATTRIBUTE}`);

    private getShowData = async () => {
        if (!this.tag) {
            this.data = null;
            return;
        }

        try {
            const response = await fetch(
                `${BASE_API_URL}/v4/mercury/wells-fargo/sponsored-shows/tag/${this.tag}`
            );

            if (!response.ok) {
                let errorMessage = "Wells Fargo Sponsored Show error";
                let statusCode = response.status;

                try {
                    const errorData = await response.json();

                    if ("message" in errorData) {
                        errorMessage = errorData.message;
                    }

                    if ("statusCode" in errorData) {
                        statusCode = errorData.statusCode;
                    }
                } catch (parseError) {
                    throw new SponsorshipError(errorMessage, statusCode);
                }

                throw new SponsorshipError(errorMessage, statusCode);
            }
            this.data = await response.json();
        } catch (error) {
            this.data = null;
            Analytics.logError(error as Error, "SPONSORSHIP_TAG_ERROR");
        }
    };

    private mutationCallback = (mutations: MutationRecord[]) => {
        mutations.forEach(async mutation => {
            if (
                mutation.type === "attributes" &&
                mutation.attributeName === SponsorshipService.TAG_DATA_ATTRIBUTE
            ) {
                if (mutation.target instanceof HTMLElement) {
                    await this.rescan();
                }
            }
        });
    };

    private observe = () => {
        this.observer.observe(document.body, {
            attributes: true,
            attributeFilter: [SponsorshipService.TAG_DATA_ATTRIBUTE]
        });
    };

    public rescan = async () => {
        const tag = SponsorshipService.getTagByBodyAttribute();

        if (!tag) {
            this.tag = null;
            this.data = null;
        } else if (tag !== this.tag) {
            this.tag = tag;
            await this.getShowData();
        }

        this.emit("tagData", this.data);
    };

    public destroy = () => {
        this.observer.disconnect();
    };

    public async init() {
        await this.getShowData();
        this.observe();
    }

    public getData = () => {
        return this.data;
    };

    public getTag = () => {
        return this.tag;
    };

    public getTagDataByPlacement = (
        placement: keyof SponsoredShowPlacementsList
    ) => {
        if (this.data && this.data.placements[placement]) {
            return this.data.tag;
        }

        return null;
    };
}
